import {
  AppShell,
  Group,
  Text,
  NavLink,
  Menu,
  ScrollArea,
  UnstyledButton,
  Avatar,
  Divider,
  Badge,
  Image,
} from "@mantine/core";
import {
  IconLayoutDashboard,
  IconBriefcase2,
  IconLogout,
  IconUser,
  IconFileDescription,
} from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import "mantine-react-table/styles.css";

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
};

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get admin email from token
  const adminToken = Cookies.get("token");
  let adminEmail = "admin@gtconnect.com";
  if (adminToken) {
    try {
      const decoded = jwtDecode<DecodedToken>(adminToken);
      adminEmail = decoded.email;
    } catch (e) {
      // Ignore decode errors
    }
  }

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { label: "Companies", icon: IconLayoutDashboard, path: "/admin/companies" },
    { label: "Collaborations", icon: IconBriefcase2, path: "/admin/collaborations" },
    { label: "Interest Management", icon: IconUser, path: "/admin/interest" },
    { label: "Resource", icon: IconFileDescription, path: "/admin/resource" },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/admin/login");
  };

  return (
    <AppShell
      navbar={{
        width: 260,
        breakpoint: 0,
      }}
      padding="0"
    >
      <AppShell.Navbar p="md">
        {/* HEADER / BRANDING */}
        <AppShell.Section mb="md">
          <Group gap="xs">
            <Image src="/gtLogo.svg" alt="GT Setu" style={{ height: 38, width: "auto" }} />
            <Badge color="red" size="sm" variant="filled">
              Admin
            </Badge>
          </Group>
          <Divider />
        </AppShell.Section>

        {/* NAV LINKS */}
        <AppShell.Section grow component={ScrollArea}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                label={
                  <Text size="sm" fw={400} c={isActive(item.path) ? "primary.7" : undefined}>
                    {item.label}
                  </Text>
                }
                leftSection={
                  <item.icon
                    size="1.2rem"
                    stroke={1.8}
                    color={isActive(item.path) ? "var(--mantine-color-primary-7)" : undefined}
                  />
                }
                active={isActive(item.path)}
                onClick={() => handleNavClick(item.path)}
                variant="light"
                color="primary.7"
                styles={{
                  root: {
                    borderRadius: 8,
                    padding: "10px 12px",
                  },
                }}
              />
            ))}
          </div>
        </AppShell.Section>

        {/* ADMIN PROFILE */}
        <AppShell.Section>
          <Menu shadow="md" width={250} position="top-end">
            <Menu.Target>
              <UnstyledButton
                w="100%"
                p="xs"
                style={{
                  borderRadius: 8,
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-1)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <Group>
                  <Avatar color="red" radius="xl">
                    <IconUser size={18} />
                  </Avatar>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <Text size="sm" fw={400} truncate>
                      Admin
                    </Text>
                    <Text c="slate.6" size="xs" truncate>
                      {adminEmail}
                    </Text>
                  </div>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={handleLogout}>
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
